import { getPlots } from 'hooks/usePlots'
import RBush from 'rbush'
import { create } from 'zustand'
import { convertPixelCoordsToLatLng } from '../tileUtils'
import axios from 'axios'
import collections from 'utils/mavis/collections'

const spatialIndex = new RBush()

const usePlotsMarket = create((set, get) => ({
  map: null,
  //This is kinda weird but it's the best way I could think of to combine the two data sources
  //Basically, by default, we're going to use the plotsData from the .json file
  //BUT, we need to transform it into something that resivoir/listing page likes
  //With images, and a token object
  //Then we can just pass this to the collection page
  plots: [],

  setPlots: async (plots: any) => {
    spatialIndex.clear()

    const transformedPlots = plots.map((plot: any) => {
      // Not using the demoProperties for now
      // Just creating our own unified plot object
      const newPlot = {
        id: `property${plot.local_id}`,
        imageUrl: `/maps/plots/photos/${plot.local_id}.png`,
        token: {
          tokenId: plot.id,
          name: `Plot ${plot.local_id} in ${plot.biome}`,
          aspectRatio: '3/2',
          attributes: {
            biome: plot.biome,
            // size: 'City',
          },
        },
        ...plot,
      }
      if (newPlot.position) {
        const corners = convertPixelCoordsToLatLng(newPlot.position, 11)
        const propertyBounds = {
          minLat: Math.min(...corners.map(([lat]) => lat)),
          minLng: Math.min(...corners.map(([, lng]) => lng)),
          maxLat: Math.max(...corners.map(([lat]) => lat)),
          maxLng: Math.max(...corners.map(([, lng]) => lng)),
        }
        spatialIndex.insert({
          minX: propertyBounds.minLng,
          minY: propertyBounds.minLat,
          maxX: propertyBounds.maxLng,
          maxY: propertyBounds.maxLat,
          property: newPlot,
        })
      }
      return newPlot
    })

    // console.log('Transformed plots: ', transformedPlots.length)
    // console.log('First 5: ', transformedPlots.slice(0, 5))

    set({ plots: transformedPlots })
  },
  coordinates: { lat: 0, lng: 0, zoom: 0 },
  filters: {},
  //selectedPlot is mainly used to display the property details in the modal
  selectedPlot: null,
  filteredProperties: [],
  filteredPropertiesWithMarketData: {},
  rightPanelProperties: [],
  setCoordinates: (coordinates: any) => set({ coordinates }),
  //Probably don't need this unless we want custom filters not provided by the side collection menu
  setFilters: (filters: any) => set({ filters }),
  //Main function to set the selected plot
  setSelectedPlot: (selectedPlot: any) => set({ selectedPlot }),

  //Sets the map object
  //So we can use it to calculate the filtered properties
  //And maybe do other things with it
  setMap: (map: any) => {
    // console.log('map', map)
    set({ map })
  },

  //Sooooo
  //Maybe eventually we can use this to fetch the plots from the Bisonic API
  //With all the data we need and we don't have to do this whole
  //Thing with the json file
  //But for now, we're just going to use the json file
  //I suppose we're working with three data sources now
  // 1. The .json file
  // 2. The Reservoir API
  // 3. The Bisonic API (eventually)
  fetchPlots: async () => {
    const newPlots = await getPlots()

    // @ts-ignore
    await get().setPlots(newPlots)
  },

  // calculateFilteredProperties is a function that will calculate the properties that are currently in view
  calculateFilteredProperties: async () => {
    if (typeof window === 'undefined') return

    // console.log('Calculating filtered properties')
    const { map }: any = usePlotsMarket.getState()

    // Filter properties within the map bounds
    const filtered = map
      ? spatialIndex
          .search({
            minX: map.getBounds().getSouthWest().lng,
            minY: map.getBounds().getSouthWest().lat,
            maxX: map.getBounds().getNorthEast().lng,
            maxY: map.getBounds().getNorthEast().lat,
          })
          .map((item: any) => item.property)
      : []

    // console.log('Filtered properties: ', filtered.length)

    set({ filteredProperties: filtered })

    // Fetch market data for the first 50 filtered properties
    const propertiesToFetch = filtered.slice(0, 50)

    // console.log('Properties to fetch: ', propertiesToFetch)

    const tokenIds = propertiesToFetch.map(
      (plot) => `${collections.runiverseLand}:${plot.id}`
    )

    //   collection: collectionAddress,
    //   tokens: tokenAddressAndId,
    //   limit,
    //   continuation,
    //   sortBy,
    //   sortDirection,
    //   tokenName,
    // } = req.query

    try {
      const response = await axios.get('/api/mavis/ronin/tokens/v6', {
        params: {
          collection: collections.runiverseLand,
          tokens: tokenIds,
          continuation: 0,
          limit: 50,
        },
      })

      // console.log('Market data response: ', response.data)

      const marketData = response.data.tokens

      // Merge market data with existing plot data
      const mergedProperties = propertiesToFetch.map((plot) => {
        const matchingMarketData = marketData.find(
          (data: any) =>
            data.token.tokenId.toString() === plot.token.tokenId.toString()
        )
        return {
          ...plot,
          marketData: matchingMarketData,
        }
      })

      // console.log('Filtered properties with market data: ', mergedProperties)
      //convert to object with tokenId as key
      const mergedPropertiesObject = mergedProperties.reduce(
        (acc, plot) => {
          acc[plot.token.tokenId] = plot
          return acc
        },
        // @ts-ignore
        get().filteredPropertiesWithMarketData
      )

      set({ filteredPropertiesWithMarketData: mergedPropertiesObject })
    } catch (error) {
      console.error('Error fetching market data:', error)
    }
  },

  setRightPanelProperties: () => {
    // console.log('Setting right panel properties')

    const { filteredProperties }: any = usePlotsMarket.getState()
    // console.log('Filtered properties: ', filteredProperties)
    set({
      rightPanelProperties: filteredProperties,
    })
  },

  clearFilteredProperties: () => {
    if (typeof window === 'undefined') return

    // @ts-ignore
    const { map, plots } = get()

    // Clear the filtered properties and market data
    set({
      filteredProperties: [],
      filteredPropertiesWithMarketData: {},
      rightPanelProperties: [], // Also clear the right panel
    })

    // Reset the spatial index
    spatialIndex.clear()

    // Rebuild the spatial index with all plots

    // @ts-ignore
    plots.forEach((plot) => {
      if (plot.position) {
        const corners = convertPixelCoordsToLatLng(plot.position, 11)
        const propertyBounds = {
          minLat: Math.min(...corners.map(([lat]) => lat)),
          minLng: Math.min(...corners.map(([, lng]) => lng)),
          maxLat: Math.max(...corners.map(([lat]) => lat)),
          maxLng: Math.max(...corners.map(([, lng]) => lng)),
        }
        spatialIndex.insert({
          minX: propertyBounds.minLng,
          minY: propertyBounds.minLat,
          maxX: propertyBounds.maxLng,
          maxY: propertyBounds.maxLat,
          property: plot,
        })
      }
    })

    // Force a recalculation of filtered properties
    if (map) {
      // @ts-ignore
      get().calculateFilteredProperties()
    }
  },

  // centerMapOnProperty: (plot: any) => {
  //   if (typeof window === 'undefined') return
  //   // console.log('Centering map on property', plot)
  //   const { map, plots }: any = usePlotsMarket.getState()

  //   const property = plots.find((p: any) => p.id === plot.token.tokenId)

  //   if (property && property.position) {
  //     const corners = convertPixelCoordsToLatLng(property.position, 11)
  //     const center = [
  //       (corners[0][0] + corners[2][0]) / 2,
  //       (corners[0][1] + corners[2][1]) / 2,
  //     ]

  //     if (map) {
  //       map.setView(center, 11)
  //     }
  //   }
  // },
}))

export default usePlotsMarket
